import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from './AuthContext';

const LocationContext = createContext(null);

export const useLocation = () => useContext(LocationContext);

export const LocationProvider = ({ children }) => {
  const { business, isAuthenticated } = useAuth();
  const [locations, setLocations] = useState([]);
  const [activeLocation, setActiveLocationState] = useState(null);
  const [userRole, setUserRole] = useState('owner');
  const [loadingLoc, setLoadingLoc] = useState(true);
  // isAdminView: chain owner üçün "İdarəedici hesab" modu
  const [isAdminView, setIsAdminViewState] = useState(() => {
    return localStorage.getItem('chain_admin_view') !== 'false';
  });

  // Load locations and determine role
  const loadLocations = useCallback(async () => {
    if (!business?.id) { setLoadingLoc(false); return; }
    setLoadingLoc(true);
    try {
      // 1. Check if this auth user is a branch manager (not owner)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const isOwner = business.owner_id === user.id;

      if (!isOwner) {
        // Manager: load assigned location
        const { data: bu } = await supabase
          .from('business_users')
          .select('id, role, location_id')
          .eq('auth_user_id', user.id)
          .eq('business_id', business.id)
          .single();

        if (bu?.location_id) {
          const { data: loc } = await supabase
            .from('business_locations')
            .select('*')
            .eq('id', bu.location_id)
            .single();

          if (loc) {
            setLocations([loc]);
            setActiveLocationState(loc);
          }
        }
        setUserRole(bu?.role || 'manager');
        setLoadingLoc(false);
        return;
      }

      // Owner: load all locations
      setUserRole('owner');
      const { data: locs } = await supabase
        .from('business_locations')
        .select('*')
        .eq('business_id', business.id)
        .order('is_main', { ascending: false })
        .order('created_at', { ascending: true });

      const allLocs = locs || [];
      setLocations(allLocs);

      // Restore last selected location from localStorage
      const savedId = localStorage.getItem(`active_location_${business.id}`);
      const saved = allLocs.find(l => l.id === savedId);
      setActiveLocationState(saved || allLocs[0] || null);

    } catch (e) {
      console.error('LocationContext error:', e);
    } finally {
      setLoadingLoc(false);
    }
  }, [business?.id, business?.owner_id]);

  useEffect(() => {
    if (isAuthenticated) loadLocations();
  }, [isAuthenticated, loadLocations]);

  const setActiveLocation = useCallback((loc) => {
    setActiveLocationState(loc);
    if (business?.id) {
      localStorage.setItem(`active_location_${business.id}`, loc?.id || '');
    }
  }, [business?.id]);

  const setAdminView = useCallback((val) => {
    setIsAdminViewState(val);
    localStorage.setItem('chain_admin_view', val ? 'true' : 'false');
  }, []);

  const refreshLocations = useCallback(() => loadLocations(), [loadLocations]);

  const value = useMemo(() => ({
    locations,
    activeLocation,
    setActiveLocation,
    userRole,
    loadingLoc,
    refreshLocations,
    isOwner: userRole === 'owner',
    locationId: activeLocation?.id || null,
    isAdminView,
    setAdminView,
  }), [locations, activeLocation, setActiveLocation, userRole, loadingLoc, refreshLocations, isAdminView, setAdminView]);

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};
